// 화면 19 — 종목 90일 score 시계열 (P0).
// 권위: ARCH_DASHBOARD §4.19 / §6.2 — GET /api/v1/stocks/{symbol}/score-history?days=90.
//
// SVG 자체 구현 (Phase B BandHistoryDrawer 패턴 재사용).
// 시각 신호 4중 (Phase B 패턴 그대로):
//   (a) 상단 strip — 일자별 band 색상 (A/B/C/unclassified). Top45 미진입은 hatch overlay.
//   (b) 가운데 line — dense final_score.
//   (c) 작은 baseline dot — 매 일자 baseline marker (Top45 미진입).
//   (d) 큰 진입 마커 — in_top45=true 일자 강조 (r=4, stroke).
//
// 호버 시 SVG <title> 으로 모든 필드 표시:
//   trading_date / band / in_top45 / rank_in_sector / sector / final_score / perf_score /
//   soft_rank / volume_rank / percentile.
// (Phase B 응답에는 rank/sector 가 있었지만 화면 19 응답에는 없음 → sector 는 ScoreHistory.sector 메타로,
//  rank_in_sector 는 노출하지 않음. ARCH §6.2 응답 spec 권위.)

import { memo, useMemo } from 'react';
import { bandColor, bandSoftBg, type ScoreHistoryRow } from './types';

interface Props {
  rows: ScoreHistoryRow[];
  sector: string | null;
}

const VIEW_W = 960;
const VIEW_H = 280;
const PAD = { top: 14, right: 16, bottom: 36, left: 48 };
const STRIP_H = 22;
const HATCH_ID = 'sse-score-hatch';

export const ScoreHistoryChart = memo(function ScoreHistoryChart({
  rows,
  sector,
}: Props): JSX.Element {
  const sorted = useMemo(
    () => [...rows].sort((a, b) => a.trading_date.localeCompare(b.trading_date)),
    [rows]
  );

  const { dates, scores, yMin, yMax } = useMemo(() => {
    const ds: string[] = [];
    const ss: number[] = [];
    let mn = Infinity;
    let mx = -Infinity;
    for (const r of sorted) {
      ds.push(r.trading_date);
      if (r.final_score != null) {
        ss.push(r.final_score);
        if (r.final_score < mn) mn = r.final_score;
        if (r.final_score > mx) mx = r.final_score;
      } else {
        ss.push(NaN);
      }
    }
    if (!Number.isFinite(mn)) {
      mn = 0;
      mx = 1;
    } else if (mn === mx) {
      mn -= 1;
      mx += 1;
    }
    return { dates: ds, scores: ss, yMin: mn, yMax: mx };
  }, [sorted]);

  const plotW = VIEW_W - PAD.left - PAD.right;
  const plotH = VIEW_H - PAD.top - PAD.bottom - STRIP_H - 8;
  const stripY = PAD.top;
  const lineTop = PAD.top + STRIP_H + 8;

  const xOf = (i: number): number => {
    if (dates.length === 0) return PAD.left;
    if (dates.length === 1) return PAD.left + plotW / 2;
    return PAD.left + (i / (dates.length - 1)) * plotW;
  };

  const yOf = (s: number): number =>
    lineTop + (1 - (s - yMin) / (yMax - yMin)) * plotH;

  const linePath = useMemo(() => {
    const parts: string[] = [];
    let pendingMove = true;
    for (let i = 0; i < scores.length; i += 1) {
      const s = scores[i];
      if (Number.isNaN(s)) {
        pendingMove = true;
        continue;
      }
      const x = xOf(i);
      const y = yOf(s);
      if (pendingMove) {
        parts.push(`M ${x.toFixed(2)} ${y.toFixed(2)}`);
        pendingMove = false;
      } else {
        parts.push(`L ${x.toFixed(2)} ${y.toFixed(2)}`);
      }
    }
    return parts.join(' ');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scores, dates.length, yMin, yMax]);

  const stripWidth = dates.length > 1 ? plotW / (dates.length - 1) : plotW;
  const halfW = stripWidth / 2;

  const yTicks = useMemo(() => {
    const n = 4;
    const arr: number[] = [];
    for (let i = 0; i <= n; i += 1) arr.push(yMin + ((yMax - yMin) * i) / n);
    return arr;
  }, [yMin, yMax]);

  const xTickIdx = useMemo(() => {
    if (dates.length === 0) return [] as number[];
    if (dates.length === 1) return [0];
    const k = 5;
    const arr = new Set<number>([0, dates.length - 1]);
    for (let i = 1; i < k - 1; i += 1) {
      arr.add(Math.round((i * (dates.length - 1)) / (k - 1)));
    }
    return [...arr].sort((a, b) => a - b);
  }, [dates.length]);

  const formatScore = (v: number | null): string =>
    v == null ? '-' : v.toFixed(2);
  const formatPct = (v: number | null): string =>
    v == null ? '-' : (v * 100).toFixed(1) + '%';

  return (
    <svg
      className="sse-score-chart"
      viewBox={`0 0 ${VIEW_W} ${VIEW_H}`}
      preserveAspectRatio="none"
      role="img"
      aria-label="종목 90일 score 시계열 + Top45 진입 마커"
    >
      <defs>
        <pattern
          id={HATCH_ID}
          patternUnits="userSpaceOnUse"
          width={6}
          height={6}
          patternTransform="rotate(45)"
        >
          <line
            x1={0}
            y1={0}
            x2={0}
            y2={6}
            stroke="var(--ink-faint)"
            strokeWidth={1}
            opacity={0.5}
          />
        </pattern>
      </defs>

      {yTicks.map((t) => {
        const y = yOf(t);
        return (
          <line
            key={`grid-${t}`}
            className="sse-score-chart__grid"
            x1={PAD.left}
            x2={VIEW_W - PAD.right}
            y1={y}
            y2={y}
          />
        );
      })}
      {yTicks.map((t) => (
        <text
          key={`yt-${t}`}
          className="sse-score-chart__tick"
          x={PAD.left - 6}
          y={yOf(t) + 3}
          textAnchor="end"
        >
          {t.toFixed(1)}
        </text>
      ))}
      {xTickIdx.map((i) => (
        <text
          key={`xt-${i}`}
          className="sse-score-chart__tick"
          x={xOf(i)}
          y={VIEW_H - PAD.bottom + 14}
          textAnchor="middle"
        >
          {dates[i]?.slice(5)}
        </text>
      ))}

      {/* (a) 상단 band strip */}
      {sorted.map((r, i) => {
        const cx = xOf(i);
        return (
          <rect
            key={`strip-${r.trading_date}`}
            className={
              'sse-score-chart__band-strip' +
              (r.in_top45 ? ' sse-score-chart__band-strip--top45' : '')
            }
            x={cx - halfW}
            y={stripY}
            width={Math.max(stripWidth, 1.5)}
            height={STRIP_H}
            fill={bandColor(r.band)}
          >
            <title>
              {r.trading_date} · band {r.band ?? 'unclassified'} ·{' '}
              {r.in_top45 ? 'Top45 진입' : 'Top45 미진입'}
              {'\n'}sector: {sector ?? '-'}
              {'\n'}final_score: {formatScore(r.final_score)}
              {'\n'}perf_score: {formatScore(r.perf_score)}
              {'\n'}soft_rank: {formatScore(r.soft_rank)}
              {'\n'}volume_rank: {formatScore(r.volume_rank)}
              {'\n'}percentile: {formatPct(r.final_score_percentile)}
            </title>
          </rect>
        );
      })}

      {/* Top45 미진입 hatch overlay */}
      {sorted.map((r, i) => {
        if (r.in_top45) return null;
        const cx = xOf(i);
        return (
          <rect
            key={`hatch-${r.trading_date}`}
            x={cx - halfW}
            y={stripY}
            width={Math.max(stripWidth, 1.5)}
            height={STRIP_H}
            fill={`url(#${HATCH_ID})`}
            pointerEvents="none"
          />
        );
      })}

      <line
        className="sse-score-chart__axis"
        x1={PAD.left}
        x2={PAD.left}
        y1={lineTop}
        y2={lineTop + plotH}
      />
      <line
        className="sse-score-chart__axis"
        x1={PAD.left}
        x2={VIEW_W - PAD.right}
        y1={lineTop + plotH}
        y2={lineTop + plotH}
      />

      {/* (b) dense final_score line */}
      {linePath && <path className="sse-score-chart__line" d={linePath} />}

      {/* (c) baseline dot — Top45 미진입 */}
      {sorted.map((r, i) => {
        if (r.final_score == null) return null;
        if (r.in_top45) return null;
        return (
          <circle
            key={`bm-${r.trading_date}`}
            className="sse-score-chart__marker"
            cx={xOf(i)}
            cy={yOf(r.final_score)}
            r={1.6}
            fill={bandColor(r.band)}
            opacity={0.45}
          />
        );
      })}

      {/* (d) Top45 진입 마커 */}
      {sorted.map((r, i) => {
        if (!r.in_top45) return null;
        if (r.final_score == null) return null;
        return (
          <circle
            key={`top45-${r.trading_date}`}
            className="sse-score-chart__marker sse-score-chart__marker--top45"
            cx={xOf(i)}
            cy={yOf(r.final_score)}
            r={4}
            fill={bandColor(r.band)}
            stroke={bandSoftBg(r.band)}
            strokeWidth={2}
          >
            <title>
              {r.trading_date} · Top45 진입 · band {r.band ?? '-'} · score{' '}
              {formatScore(r.final_score)}
              {'\n'}percentile: {formatPct(r.final_score_percentile)}
            </title>
          </circle>
        );
      })}
    </svg>
  );
});

export function ScoreHistoryChartLegend(): JSX.Element {
  return (
    <div className="sse-score-chart__legend" aria-label="차트 범례">
      <span className="sse-score-chart__legend-item">
        <span
          className="sse-score-chart__legend-swatch sse-score-chart__legend-swatch--top45"
          aria-hidden="true"
        />
        Top45 진입 일자 (큰 마커)
      </span>
      <span className="sse-score-chart__legend-item">
        <span
          className="sse-score-chart__legend-swatch sse-score-chart__legend-swatch--hatch"
          aria-hidden="true"
        />
        Top45 미진입 일자 (빗금 strip)
      </span>
      <span className="sse-score-chart__legend-item">
        <span
          className="sse-score-chart__legend-swatch"
          style={{ background: 'var(--mark-up)' }}
        />
        band A
      </span>
      <span className="sse-score-chart__legend-item">
        <span
          className="sse-score-chart__legend-swatch"
          style={{ background: 'var(--warn)' }}
        />
        band B
      </span>
      <span className="sse-score-chart__legend-item">
        <span
          className="sse-score-chart__legend-swatch"
          style={{ background: 'var(--mark-down)' }}
        />
        band C
      </span>
      <span className="sse-score-chart__legend-item">
        <span
          className="sse-score-chart__legend-swatch"
          style={{ background: 'var(--ink-faint)' }}
        />
        unclassified
      </span>
    </div>
  );
}
